import { useQuery } from '@apollo/client';
import { format } from 'date-fns';

import Status from 'components/atoms/status';

import { getOpenTasksForStudents } from 'queries/tasks';

function priorityLabel(priority?: number | null) {
  if (priority === 1) return 'High Priority';
  if (priority === 2) return 'Medium Priority';
  if (priority === 3) return 'Low Priority';
  return null;
}

export default function RelatedTasks({
  studentIds,
  currentTaskId,
  onOpenTask,
}: {
  studentIds: string[];
  currentTaskId?: string;
  onOpenTask?: (taskId: string) => void;
}) {
  const { data, loading } = useQuery(getOpenTasksForStudents, {
    variables: {
      userIds: studentIds,
      excludeTaskId: currentTaskId,
    },
    skip: !studentIds?.length || !currentTaskId,
    fetchPolicy: 'network-only',
  });

  const relatedTasks = data?.tasks ?? [];

  if (loading) {
    return <p className="text-sm text-gray-400">Loading...</p>;
  }

  if (relatedTasks.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No other open tasks for this student.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-200 overflow-hidden rounded border border-gray-300 bg-white">
      {relatedTasks.map((task) => {
        const priority = priorityLabel(task.priority);
        return (
          <li key={task.id}>
            <button
              type="button"
              onClick={() => onOpenTask?.(task.id)}
              disabled={!onOpenTask}
              className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-gray-50 disabled:cursor-default"
            >
              <span className="text-sm font-medium text-gray-900">
                {task.title}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {task.status?.value ? (
                  <span className="text-[11px] font-semibold uppercase text-gray-500">
                    {String(task.status.value).replaceAll('_', ' ')}
                  </span>
                ) : null}
                {priority ? <Status value={priority} /> : null}
              </div>
              {task.due ? (
                <span className="text-xs text-gray-500">
                  Due {format(new Date(task.due), 'do MMM yyyy HH:mm')}
                </span>
              ) : null}
              {task.assignee ? (
                <span className="text-xs text-gray-500">
                  {task.assignee.given_name} {task.assignee.family_name}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
